import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent, initFacebookPixel } from '../utils/analytics';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  version: number;
}

interface CookieDialogProps {
  isVisible: boolean;
  onClose: () => void;
}

const CookieIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '24px' }}>
    <path d="M21.5 12C21.5 17.2467 17.2467 21.5 12 21.5C6.75329 21.5 2.5 17.2467 2.5 12C2.5 6.75329 6.75329 2.5 12 2.5C13.5 2.5 14.9 2.8 16.2 3.4C16.1 3.9 16.1 4.4 16.3 4.9C16.5 5.4 16.9 5.8 17.4 6.1C17.9 6.4 18.5 6.5 19 6.4C19.5 6.3 20 6 20.4 5.6C21.1 7.5 21.5 9.7 21.5 12Z" stroke="#77F76C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 8.5C10.5523 8.5 11 8.05228 11 7.5C11 6.94772 10.5523 6.5 10 6.5C9.44772 6.5 9 6.94772 9 7.5C9 8.05228 9.44772 8.5 10 8.5Z" fill="#77F76C"/>
    <path d="M15 12.5C15.5523 12.5 16 12.0523 16 11.5C16 10.9477 15.5523 10.5 15 10.5C14.4477 10.5 14 10.9477 14 11.5C14 12.0523 14.4477 12.5 15 12.5Z" fill="#77F76C"/>
    <path d="M9 16.5C9.55228 16.5 10 16.0523 10 15.5C10 14.9477 9.55228 14.5 9 14.5C8.44772 14.5 8 14.9477 8 15.5C8 16.0523 8.44772 16.5 9 16.5Z" fill="#77F76C"/>
  </svg>
);

const CookieDialog: React.FC<CookieDialogProps> = ({ isVisible, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Cookie-Hilfsfunktionen
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  };

  // Cookie-Präferenzen speichern
  const savePreferences = (preferences: CookiePreferences) => {
    setCookie("cookieConsent", JSON.stringify(preferences), 365);
    applyConsentPreferences(preferences);
    onClose();
  };

  // Analytics aktivieren/deaktivieren
  const enableAnalytics = () => {
    try {
      // Umami Event
      trackEvent('Cookie_Analytics_Accepted');

      // Facebook Pixel Init (will check consent internally too, but we are enabling it here)
      initFacebookPixel();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).amplitude) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.setOptOut(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.getInstance().logEvent('Cookie_Analytics_Accepted');
        console.log("Amplitude Analytics aktiviert");
      }
    } catch (e) {
      console.error("Fehler beim Aktivieren von Analytics:", e);
    }
  };

  const disableAnalytics = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).amplitude) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.setOptOut(true);
        console.log("Amplitude Analytics deaktiviert");
      }
    } catch (e) {
      console.error("Fehler beim Deaktivieren von Analytics:", e);
    }
  };

  const applyConsentPreferences = (preferences: CookiePreferences) => {
    if (preferences.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  };

  // Event Handler
  const handleAcceptAll = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      version: 1
    };
    savePreferences(preferences);
  };

  const handleSaveSettings = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: analyticsEnabled,
      version: 1
    };
    savePreferences(preferences);
  };

  const handleDeclineAll = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      version: 1
    };
    savePreferences(preferences);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            x: '-50%',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '33vh',
            zIndex: 9999,
            backgroundColor: 'rgba(10, 10, 10, 0.85)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            color: '#E5EAEA',
            fontFamily: "'Poppins', sans-serif"
          }}
        >
          {/* Header Gradient Line */}
          <div style={{ 
            height: '2px', 
            width: '100%', 
            background: 'linear-gradient(90deg, transparent, #77F76C, transparent)',
            flexShrink: 0
          }} />

          <div style={{ 
            padding: '24px', 
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: 1,
            minHeight: 0,
            overscrollBehavior: 'contain'
          }}>
            
            {/* Title & Intro */}
            <div style={{ flexShrink: 0 }}>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '1.2rem', 
                fontWeight: 600,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <CookieIcon /> Privatsphäre-Einstellungen
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '0.9rem', 
                lineHeight: 1.5, 
                color: '#aaa' 
              }}>
                Wir nutzen Cookies, um dein Erlebnis auf unserer Website zu verbessern. 
                Einige sind essenziell, andere helfen uns, die Nutzung zu verstehen.
              </p>
            </div>

            {/* Expandable Details */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  style={{ overflow: 'hidden', flexShrink: 0 }}
                >
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    borderRadius: '12px',
                    marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Notwendig</strong>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Für die Grundfunktionen der Seite.</p>
                      </div>
                      <input type="checkbox" checked disabled style={{ accentColor: '#77F76C', transform: 'scale(1.2)' }} />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Analyse</strong>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Hilft uns, die Seite zu verbessern.</p>
                      </div>
                      <input 
                        type="checkbox" 
                        checked={analyticsEnabled} 
                        onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                        style={{ accentColor: '#77F76C', transform: 'scale(1.2)', cursor: 'pointer' }} 
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px', 
              marginTop: 'auto',
              flexShrink: 0
            }}>
              <button
                onClick={handleAcceptAll}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#77F76C',
                  color: '#000',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  transition: 'transform 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Alle akzeptieren
              </button>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                {!showDetails ? (
                  <button
                    onClick={() => setShowDetails(true)}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'transparent',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    Einstellungen
                  </button>
                ) : (
                  <button
                    onClick={handleSaveSettings}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      borderRadius: '12px',
                      border: '1px solid #77F76C',
                      background: 'transparent',
                      color: '#77F76C',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Speichern
                  </button>
                )}

                <button
                  onClick={handleDeclineAll}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'transparent',
                    color: '#aaa',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                >
                  Ablehnen
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieDialog;
